import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useQuery } from '@apollo/client';
import { 
  Paper, 
  Typography, 
  Box, 
  Card, 
  CardContent,
  LinearProgress,
  Avatar,
  Chip,
  IconButton,
  Button,
  Stack
} from '@mui/material';
import {
  Kitchen as KitchenIcon,
  ShoppingCart as ShoppingCartIcon,
  CalendarMonth as CalendarIcon,
  Group as GroupIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { GET_ACTIVE_MEAL_PLAN, LIST_PANTRY_ITEMS, GET_EXPIRING_ITEMS } from '../graphql/queries';
import RecipeModal from '../components/RecipeModal';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [householdId] = useState('default-household'); // TODO: Get from user context
  const [selectedRecipe, setSelectedRecipe] = useState<any>(null);
  
  const { data: mealPlanData, loading: mealPlanLoading } = useQuery(GET_ACTIVE_MEAL_PLAN, {
    variables: { householdId },
    skip: !householdId
  });

  const { data: pantryData, loading: pantryLoading } = useQuery(LIST_PANTRY_ITEMS, {
    variables: { householdId },
    skip: !householdId
  });

  const { data: expiringData } = useQuery(GET_EXPIRING_ITEMS, {
    variables: { householdId, days: 7 },
    skip: !householdId
  });

  // Mock data for nutrition chart
  const nutritionData = [
    { name: 'Mon', calories: 2100, protein: 85 },
    { name: 'Tue', calories: 1950, protein: 75 },
    { name: 'Wed', calories: 2200, protein: 92 },
    { name: 'Thu', calories: 2050, protein: 80 },
    { name: 'Fri', calories: 2150, protein: 88 },
    { name: 'Sat', calories: 2300, protein: 95 },
    { name: 'Sun', calories: 2000, protein: 78 },
  ];

  // Mock data for pantry categories
  const pantryCategories = [
    { name: 'Produce', value: 25 },
    { name: 'Dairy', value: 15 },
    { name: 'Proteins', value: 20 },
    { name: 'Grains', value: 18 },
    { name: 'Other', value: 22 },
  ];

  const StatCard = ({ icon, title, value, subtitle, color, trend }: any) => (
    <Card sx={{ height: '100%', background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)` }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Box display="flex" alignItems="center" mb={1}>
              <Avatar sx={{ bgcolor: color, width: 40, height: 40, mr: 1 }}>
                {icon}
              </Avatar>
              <Typography color="text.secondary" variant="body2">
                {title}
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight="bold">
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {subtitle}
            </Typography>
          </Box>
          {trend && (
            <Box textAlign="right">
              <TrendingUpIcon sx={{ color: 'success.main' }} />
              <Typography variant="caption" color="success.main">
                {trend}
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  const todaysMeals = mealPlanData?.getActiveMealPlan?.planSlots?.filter((slot: any) => {
    const slotDate = new Date(slot.date).toDateString();
    const today = new Date().toDateString();
    return slotDate === today;
  }) || [];

  const pantryItemCount = pantryData?.listPantryItems?.length || 0;
  const expiringItemCount = expiringData?.getExpiringItems?.length || 0;

  return (
    <Box sx={{ p: 3 }}>
      <Box mb={3}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Welcome back, {user?.attributes?.email?.split('@')[0] || 'User'}!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {format(new Date(), 'EEEE, MMMM d, yyyy')}
        </Typography>
      </Box>

      {/* Quick Actions */}
      <Box mb={3} display="flex" gap={2}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          component={Link}
          to="/meal-plans"
          sx={{ 
            background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
            color: 'white',
            '&:hover': {
              background: 'linear-gradient(45deg, #FE6B8B 60%, #FF8E53 100%)',
            }
          }}
        >
          Create Meal Plan
        </Button>
        <Button
          variant="outlined"
          startIcon={<ShoppingCartIcon />}
          component={Link}
          to="/grocery-lists"
        >
          Generate Grocery List
        </Button>
        <Button
          variant="outlined"
          startIcon={<KitchenIcon />}
          component={Link}
          to="/pantry"
        >
          Add Pantry Items
        </Button>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ mb: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3 }}>
        <StatCard
          icon={<CalendarIcon />}
          title="Active Meal Plan"
          value={mealPlanData?.getActiveMealPlan ? "This Week" : "None"}
          subtitle={todaysMeals.length + " meals today"}
          color="#0088FE"
        />
        <StatCard
          icon={<KitchenIcon />}
          title="Pantry Items"
          value={pantryItemCount}
          subtitle="Total items"
          color="#00C49F"
          trend="+12%"
        />
        <StatCard
          icon={<WarningIcon />}
          title="Expiring Soon"
          value={expiringItemCount}
          subtitle="Within 7 days"
          color="#FFBB28"
        />
        <StatCard
          icon={<GroupIcon />}
          title="Household"
          value="4"
          subtitle="Members"
          color="#FF8042"
        />
      </Box>

      {/* Today's Meals and Pantry */}
      <Box sx={{ mb: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight="bold">
              Today's Meals
            </Typography>
            <IconButton component={Link} to="/meal-plans">
              <ArrowForwardIcon />
            </IconButton>
          </Box>
          {mealPlanLoading ? (
            <LinearProgress />
          ) : todaysMeals.length > 0 ? (
            <Stack spacing={2}>
              {todaysMeals.map((meal: any, index: number) => (
                <Card variant="outlined" key={index} sx={{ cursor: 'pointer' }} onClick={() => setSelectedRecipe(meal)}>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box display="flex" alignItems="center">
                        <Avatar sx={{ bgcolor: COLORS[index % COLORS.length], mr: 2 }}>
                          {meal.mealType.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="medium">
                            {meal.recipeName || meal.mealType}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {meal.mealType} • {meal.recipeStub?.readyInMinutes || 30} minutes
                          </Typography>
                        </Box>
                      </Box>
                      <Chip 
                        label={meal.status} 
                        size="small"
                        color={meal.status === 'completed' ? 'success' : 'default'}
                        icon={meal.status === 'completed' ? <CheckCircleIcon /> : undefined}
                      />
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          ) : (
            <Box textAlign="center" py={4}>
              <Typography color="text.secondary">
                No meals planned for today
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                component={Link}
                to="/meal-plans"
                sx={{ mt: 2 }}
              >
                Create Meal Plan
              </Button>
            </Box>
          )}
        </Paper>

        {/* Pantry Overview */}
        <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight="bold">
                Pantry Overview
              </Typography>
              <IconButton component={Link} to="/pantry">
                <ArrowForwardIcon />
              </IconButton>
            </Box>
            {pantryLoading ? (
              <LinearProgress />
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={pantryCategories}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    fill="#8884d8"
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {pantryCategories.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
            <Box mt={2}>
              {pantryCategories.map((category, index) => (
                <Box key={category.name} display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Box display="flex" alignItems="center">
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: COLORS[index % COLORS.length],
                        mr: 1
                      }}
                    />
                    <Typography variant="body2">{category.name}</Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="medium">
                    {category.value}
                  </Typography>
                </Box>
              ))}
            </Box>
        </Paper>
      </Box>

      {/* Weekly Nutrition */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight="bold" mb={2}>
          Weekly Nutrition Overview
        </Typography>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={nutritionData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="calories" fill="#0088FE" name="Calories" />
            <Bar dataKey="protein" fill="#00C49F" name="Protein (g)" />
          </BarChart>
        </ResponsiveContainer>
      </Paper>
      <RecipeModal open={!!selectedRecipe} onClose={() => setSelectedRecipe(null)} recipe={selectedRecipe} />
    </Box>
  );
};

export default Dashboard;
